import React, { useState } from "react";
import "./ProficiencyModal.css";

const EuropeanDescription = () => (
  <p className="proficiency-description">
    The <strong>A1–C2 levels</strong> describe overall language proficiency across <strong>European languages</strong>— <strong>German</strong>, <strong>Spanish</strong>, <strong>Italian</strong>, and <strong>French</strong>. At <strong>A1</strong>, learners use very basic everyday expressions and simple sentences for introductions and basic needs; at <strong>A2</strong>, they communicate in routine situations and understand frequently used expressions related to daily life. At <strong>B1</strong>, learners can manage familiar topics, describe experiences, and express opinions with reasonable confidence, while at <strong>B2</strong>, they communicate fluently, understand complex texts, and discuss abstract topics clearly. At <strong>C1</strong>, learners use the language effectively for professional and academic purposes with a high level of accuracy, and at <strong>C2</strong>, they demonstrate near-native proficiency, understanding virtually everything and expressing themselves effortlessly in all contexts.
  </p>
);

const JapaneseDescription = () => (
  <p className="proficiency-description">
    The <strong>N5–N1 levels</strong> describe overall <strong>Japanese</strong> language proficiency. At <strong>N5</strong>, learners understand and use very basic Japanese expressions, simple grammar, and everyday vocabulary for daily situations; at <strong>N4</strong>, they can understand simple conversations and texts related to familiar topics and routine activities. At <strong>N3</strong>, learners handle everyday situations more independently, understand natural-speed conversations to some extent, and read texts with slightly complex structures, while at <strong>N2</strong>, they comprehend a wide range of spoken and written Japanese, including news and workplace communication, and express ideas clearly and effectively. At <strong>N1</strong>, learners demonstrate advanced, near-native proficiency, understanding complex and abstract content and using Japanese accurately and naturally in academic and professional contexts.
  </p>
);

/* Filled triangle arrows matching Figma exactly */
const ArrowDown = () => (
  <svg className="path-arrow arrow-down" viewBox="0 0 17 9" fill="none">
    <path d="M17 0L0 0L8.5 8.5L17 0Z" fill="black" />
  </svg>
);

const ArrowUp = () => (
  <svg className="path-arrow arrow-up" viewBox="0 0 17 9" fill="none">
    <path d="M17 8.5L0 8.5L8.5 0L17 8.5Z" fill="black" />
  </svg>
);

const ArrowRight = () => (
  <svg className="path-arrow arrow-right" viewBox="0 0 9 17" fill="none">
    <path d="M0 0L0 17L8.5 8.5L0 0Z" fill="black" />
  </svg>
);

const ProficiencyModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("european");

  if (!isOpen) return null;

  return (
    <>
      <div className="proficiency-modal-overlay" onClick={onClose} />
      <div
        className="proficiency-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Learning Path"
      >
        <div className="proficiency-modal-accent" />

        <div className="proficiency-modal-header">
          <h2 className="proficiency-modal-title">Learning Path</h2>
          <button
            className="proficiency-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <img src="/images/closeicon.svg" alt="Close" />
          </button>
        </div>

        <div className="proficiency-modal-tabs">
          <button
            className={`proficiency-modal-tab ${activeTab === "european" ? "active" : ""}`}
            onClick={() => setActiveTab("european")}
          >
            European Languages
          </button>
          <button
            className={`proficiency-modal-tab ${activeTab === "japanese" ? "active" : ""}`}
            onClick={() => setActiveTab("japanese")}
          >
            Japanese Language
          </button>
        </div>

        <div className="proficiency-modal-body">
          {activeTab === "european" ? <EuropeanDescription /> : <JapaneseDescription />}

          {activeTab === "european" ? (
            <div className="zigzag-container zigzag-european">
              <img
                src="/images/dashed-path-european.svg"
                alt=""
                className="dashed-path-svg european-path"
              />
              {/* Start label + arrow */}
              <div className="path-start-label eu-start">
                <span>Start</span>
                <ArrowDown />
              </div>
              {/* Top row blocks */}
              <div className="zz-block level-a1 eu-a1">
                <span>A1{"\n"}Beginner</span>
              </div>
              <div className="zz-block level-b1 eu-b1">
                <span>B1{"\n"}Pre-Intermediate</span>
              </div>
              <div className="zz-block level-c1 eu-c1">
                <span>C1{"\n"}Advanced</span>
              </div>
              {/* Bottom row blocks */}
              <div className="zz-block level-a2 eu-a2">
                <span>A2{"\n"}Elementary</span>
              </div>
              <div className="zz-block level-b2 eu-b2">
                <span>B2{"\n"}Intermediate</span>
              </div>
              <div className="zz-block level-c2 eu-c2">
                <span>C2{"\n"}Proficient</span>
              </div>
              {/* Down arrows (A1→A2, B1→B2, C1→C2) */}
              <div className="zz-arrow eu-arrow-1"><ArrowDown /></div>
              <div className="zz-arrow eu-arrow-3"><ArrowDown /></div>
              <div className="zz-arrow eu-arrow-5"><ArrowDown /></div>
              {/* Up arrows (A2→B1, B2→C1) */}
              <div className="zz-arrow eu-arrow-2"><ArrowUp /></div>
              <div className="zz-arrow eu-arrow-4"><ArrowUp /></div>
              {/* Finish */}
              <div className="path-finish-label eu-finish">
                <span>Finish</span>
              </div>
            </div>
          ) : (
            <div className="zigzag-container zigzag-japanese">
              <img
                src="/images/dashed-path-japanese.svg"
                alt=""
                className="dashed-path-svg japanese-path"
              />
              {/* Start label + arrow */}
              <div className="path-start-label jp-start">
                <span>Start</span>
                <ArrowDown />
              </div>
              {/* Top row blocks */}
              <div className="zz-block level-n5 jp-n5">
                <span>N5{"\n"}Beginner</span>
              </div>
              <div className="zz-block level-n3 jp-n3">
                <span>N3{"\n"}Intermediate</span>
              </div>
              <div className="zz-block level-n1 jp-n1">
                <span>N1{"\n"}Proficient</span>
              </div>
              {/* Bottom row blocks */}
              <div className="zz-block level-n4 jp-n4">
                <span>N4{"\n"}Pre-Intermediate</span>
              </div>
              <div className="zz-block level-n2 jp-n2">
                <span>N2{"\n"}Advanced</span>
              </div>
              {/* Down arrows (N5→N4, N3→N2) */}
              <div className="zz-arrow jp-arrow-1"><ArrowDown /></div>
              <div className="zz-arrow jp-arrow-3"><ArrowDown /></div>
              {/* Up arrows (N4→N3, N2→N1) */}
              <div className="zz-arrow jp-arrow-2"><ArrowUp /></div>
              <div className="zz-arrow jp-arrow-4"><ArrowUp /></div>
              {/* Finish with right arrow */}
              <div className="path-finish-label jp-finish">
                <ArrowRight />
                <span>Finish</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProficiencyModal;
